import { useEffect, useRef, useState } from "react";
import { UserCheck, Calendar } from "lucide-react";
import nikitaAd from "@/assets/nikita.mp4.asset.json";
import alexNascarAd from "@/assets/alex-nascar.mp4.asset.json";

// Publicités existantes du projet
const ADS = [alexNascarAd.url, nikitaAd.url];
// Cadrage par vidéo (évite de couper les visages)
const AD_POSITIONS = ["center 50%", "center 38%"];
// Départ de lecture par vidéo (Alex : segment avec la Ferrari derrière)
const AD_START_TIMES = [6, 0];

const AD_DURATION = 2000;
const SCENE_DURATIONS = [ADS.length * AD_DURATION, 2400, 3400];
const LABELS = ["CONTENU", "PUBLICITÉ", "RÉSULTATS"];

const MINT = "#08E4A7";

export default function HeroSystemCard() {
  const [scene, setScene] = useState(0);
  const [adIndex, setAdIndex] = useState(0);
  const [count, setCount] = useState(0);
  const [notif1, setNotif1] = useState(false);
  const [notif2, setNotif2] = useState(false);
  const [instant, setInstant] = useState(false);
  const reduced = useRef(false);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const runScene = (s: number) => {
    clearTimers();
    setScene(s);
    setInstant(false);

    if (s === 0) {
      setAdIndex(0);
      setCount(0);
      setNotif1(false);
      setNotif2(false);
      for (let i = 1; i < ADS.length; i++) {
        later(() => setAdIndex(i), i * AD_DURATION);
      }
    }
    if (s === 2) {
      let v = 0;
      const iv = window.setInterval(() => {
        v++;
        setCount(v);
        if (v >= 20) window.clearInterval(iv);
      }, 55);
      timers.current.push(iv as unknown as number);
      later(() => setNotif1(true), 900);
      later(() => setNotif2(true), 1500);
    }

    later(() => {
      const nextScene = (s + 1) % 3;
      if (nextScene === 0) {
        setInstant(true);
        later(() => runScene(0), 60);
      } else {
        runScene(nextScene);
      }
    }, SCENE_DURATIONS[s]);
  };

  useEffect(() => {
    reduced.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced.current) {
      setScene(2);
      setCount(20);
      setNotif1(true);
      setNotif2(true);
      return;
    }
    runScene(0);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sceneStyle = (n: number): React.CSSProperties => ({
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    transition: instant ? "none" : "transform 0.7s cubic-bezier(0.4,0,0.2,1), opacity 0.7s",
    transform: n === scene ? "translateY(0)" : n < scene ? "translateY(-100%)" : "translateY(100%)",
    opacity: n === scene ? 1 : 0,
  });

  const notifStyle = (on: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "9px 12px",
    borderRadius: 10,
    background: "#141816",
    border: "1px solid #22302a",
    color: "#c9d4cf",
    fontSize: 12,
    transform: on ? "translateX(0)" : "translateX(120%)",
    opacity: on ? 1 : 0,
    transition: instant ? "none" : "transform 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.5s",
  });

  return (
    <div
      aria-hidden="true"
      className="hero-system-card"
      style={{
        fontFamily: "Inter, sans-serif",
        width: "100%",
        maxWidth: 340,
        margin: "0 auto",
        background: "#0d0f0e",
        border: "1px solid #1e2422",
        borderRadius: 20,
        padding: 20,
        animation: "hsc-float 5s ease-in-out infinite",
      }}
    >
      <style>{`
        @keyframes hsc-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes hsc-pulse { 0% { box-shadow: 0 0 0 0 rgba(8,228,167,0.5); } 70% { box-shadow: 0 0 0 9px rgba(8,228,167,0); } 100% { box-shadow: 0 0 0 0 rgba(8,228,167,0); } }
        @keyframes hsc-ring { 0% { transform: scale(0.5); opacity: 0.7; } 100% { transform: scale(2.6); opacity: 0; } }
        @media (prefers-reduced-motion: reduce) {
          .hero-system-card, .hero-system-card * { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* Badge */}
      <div style={{ display: "flex", marginBottom: 14 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "#141816",
            border: "1px solid #22302a",
            borderRadius: 999,
            padding: "6px 12px",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: MINT,
              animation: "hsc-pulse 2s infinite",
            }}
          />
          <span style={{ fontSize: 11, letterSpacing: "0.12em", color: "#c9d4cf" }}>
            {LABELS[scene]}
          </span>
        </div>
      </div>

      {/* Scènes */}
      <div
        style={{
          position: "relative",
          height: 310,
          overflow: "hidden",
          borderRadius: 14,
          background: "#101312",
        }}
      >
        {/* Scène 1 — carrousel de 4 pubs */}
        <div style={sceneStyle(0)}>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 238,
              borderRadius: 12,
              overflow: "hidden",
              background: "#080a09",
            }}
          >
            {ADS.map((src, i) =>
              i === adIndex || i === (adIndex + 1) % ADS.length ? (
                <video
                  key={src}
                  src={src}
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => {
                    const start = AD_START_TIMES[i] ?? 0;
                    if (start > 0) e.currentTarget.currentTime = start;
                  }}
                  onTimeUpdate={(e) => {
                    const start = AD_START_TIMES[i] ?? 0;
                    if (start > 0 && e.currentTarget.currentTime < start - 0.2) {
                      e.currentTarget.currentTime = start;
                    }
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: AD_POSITIONS[i] ?? "center 30%",
                    opacity: i === adIndex ? 1 : 0,
                    transform: i === adIndex ? "translateX(0)" : "translateX(6%)",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                  }}
                />
              ) : null
            )}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                padding: "10px 12px",
                background: "linear-gradient(to top, rgba(8,10,9,0.9), transparent)",
              }}
            >
              <p style={{ fontSize: 11, color: "#c9d4cf", margin: "0 0 6px" }}>Diffusion Meta</p>
              <div style={{ height: 3, borderRadius: 999, background: "#22302a", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    background: MINT,
                    width: scene === 0 ? `${((adIndex + 1) / ADS.length) * 100}%` : "100%",
                    transition: instant ? "none" : `width ${AD_DURATION}ms linear`,
                  }}
                />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            {ADS.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: i === adIndex ? MINT : "#22302a",
                  transition: "background 0.3s",
                }}
              />
            ))}
          </div>
        </div>

        {/* Scène 2 — publicité activée */}
        <div style={sceneStyle(1)}>
          <div
            style={{
              position: "relative",
              width: 10,
              height: 10,
              marginBottom: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: 999, background: MINT }} />
            <span
              style={{
                position: "absolute",
                width: 28,
                height: 28,
                borderRadius: 999,
                border: `1px solid ${MINT}`,
                animation: "hsc-ring 2.2s ease-out infinite",
              }}
            />
            <span
              style={{
                position: "absolute",
                width: 28,
                height: 28,
                borderRadius: 999,
                border: `1px solid ${MINT}`,
                animation: "hsc-ring 2.2s ease-out infinite",
                animationDelay: "1.1s",
              }}
            />
          </div>
          <p style={{ fontSize: 24, color: "#FFFFFF", margin: 0 }}>Publicité activée</p>
          <p style={{ fontSize: 13, color: "#8a938f", marginTop: 8 }}>Amplification en cours</p>
        </div>

        {/* Scène 3 — résultats */}
        <div style={sceneStyle(2)}>
          <p style={{ fontSize: 10, letterSpacing: "0.18em", color: MINT, margin: 0 }}>RÉSULTATS</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 46, fontWeight: 600, color: MINT, lineHeight: 1 }}>+{count}</span>
            <span style={{ fontSize: 15, color: "#FFFFFF" }}>leads</span>
          </div>
          <p style={{ fontSize: 12, color: "#8a938f", marginTop: 6 }}>qualifiés / semaine</p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              width: "100%",
              marginTop: 22,
              overflow: "hidden",
            }}
          >
            <div style={notifStyle(notif1)}>
              <UserCheck size={14} color={MINT} />
              <span>Nouveau lead qualifié</span>
            </div>
            <div style={notifStyle(notif2)}>
              <Calendar size={14} color={MINT} />
              <span>Appel réservé au calendrier</span>
            </div>
          </div>
        </div>
      </div>

      {/* Indicateurs de scène */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 16 }}>
        {[0, 1, 2].map((n) => (
          <span
            key={n}
            style={{
              width: 18,
              height: 3,
              borderRadius: 999,
              background: n === scene ? MINT : "#22302a",
              transition: "background 0.3s",
            }}
          />
        ))}
      </div>
    </div>
  );
}
